import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  MenuTextMatch,
} from '@lexical/react/LexicalTypeaheadMenuPlugin';
import { Box, Typography } from '@mui/material';
import {
  $createTextNode,
  $getSelection,
  $insertNodes,
  $isRangeSelection,
  $isTextNode,
  TextNode,
} from 'lexical';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { arity, BUILTINS, kpiSectionLabel } from '../../formula-engine';
import {
  displayPathKey,
  displaySegmentOf,
  FieldRefTarget,
  FormulaFieldSource,
  isFormulaRecord,
  KPI_PERIOD_TARGETS,
  KPI_SECTION_TARGETS,
} from '../../util/formula-vocabulary';
import { buildRefLookups, resolveRef } from './AutoTokenizePlugin';
import {
  $enclosingCallsAtCaret,
  EnclosingCall,
  findEnclosingCalls,
  isKpiNameSlot,
  isKpiPeriodSlot,
  isKpiScenarioSlot,
  kpiSlotKpi,
  memberSlotList,
  isListSlot,
  listsInScope,
  SLOT_OPENER,
  slotKind,
} from './enclosingCalls';
import { $createFieldChipNode, FieldChipNode, fieldChipVariant } from './FieldChipNode';
import { $displayOffsetOf, $serializeFormula } from './serialization';
import { isInsideStringLiteral } from './StringLiteralPlugin';
import { TypeaheadBadge, TypeaheadMenu } from './TypeaheadMenu';

const SOURCE_LABEL: Record<FormulaFieldSource, string> = {
  metric: 'Metric',
  company: 'Company',
  round: 'Round',
  security: 'Security',
  formula: 'Formula',
  kpi: 'KPI',
};

export const SOURCE_BADGE: Record<FormulaFieldSource, TypeaheadBadge> = {
  metric: 'm',
  company: 'c',
  round: 'r',
  security: 's',
  formula: 'f',
  kpi: 'k',
};

/** The two texts under a field's name in the doc panel: where the ref points, and its path. */
export function fieldDetailParts(target: FieldRefTarget): { prefix: string; detail: string } {
  if (isFormulaRecord(target)) {
    return { prefix: SOURCE_LABEL[target.source], detail: `${target.path} · record` };
  }
  if (target.list) {
    return {
      prefix: target.list.displayName,
      detail: target.path.slice(target.list.path.length + 1),
    };
  }
  if (target.type === 'list') {
    return { prefix: SOURCE_LABEL[target.source], detail: `${target.path} · list` };
  }
  if (target.kpi) {
    // A KPI's path is an internal id the user has never seen; what they need
    // here is which arguments it accepts.
    const periods = target.kpi.periods.join(', ');
    const scenarios = target.kpi.sections.map(kpiSectionLabel).join(', ');
    return { prefix: SOURCE_LABEL[target.source], detail: `${periods} · ${scenarios}` };
  }
  if (target.kpiToken) {
    return { prefix: SOURCE_LABEL[target.source], detail: target.type };
  }
  return { prefix: SOURCE_LABEL[target.source], detail: target.path };
}

/** Doc-panel content for a field row: name, provenance, and the /column description when one exists. */
export function renderFieldDetail(target: FieldRefTarget): ReactNode {
  const { prefix, detail } = fieldDetailParts(target);
  return (
    <>
      <Typography variant='caption' component='div' sx={{ fontWeight: 700 }} color='text.primary'>
        {target.displayName}
      </Typography>
      <Typography variant='caption' component='div' color='text.secondary'>
        <Box component='span' sx={{ fontWeight: 600 }}>
          {prefix}
        </Box>
        {` · ${detail}`}
      </Typography>
      {target.description && (
        <Typography variant='caption' component='div' color='text.secondary' sx={{ mt: 0.5 }}>
          {target.description}
        </Typography>
      )}
    </>
  );
}

/**
 * An unclosed "[query" before the caret opens the menu. A custom trigger
 * because useBasicTypeaheadTriggerMatch treats "[" as punctuation and display
 * names contain spaces.
 */
export function matchFieldTrigger(text: string): MenuTextMatch | null {
  if (isInsideStringLiteral(text)) {
    return null;
  }
  const match = /\[([^\][]*)$/.exec(text);
  if (!match) {
    return null;
  }
  return { leadOffset: match.index, matchingString: match[1], replaceableString: match[0] };
}

export interface FuzzyMatch {
  score: number;
  /** Indices into the ORIGINAL text of the characters that matched, for highlighting. */
  indices: number[];
}

/** Where a word starts inside a display path: after a space or a dot. */
const WORD_SEPARATORS = new Set([' ', '.']);

/**
 * Case-insensitive subsequence match with editor-style scoring: consecutive
 * runs (+3 per link) and word starts (+3) rank a match up, a later first hit
 * ranks it down (clamped, so a strong mid-name word match still beats a weak
 * early one) — a prefix beats a scattered match. The greedy tail runs from
 * every occurrence of the first query character and the best anchor wins, so
 * "mo" against "demo Model" highlights the word start, not the "mo" in demo.
 * Returns null when the query is not a subsequence of the text.
 */
export function fuzzyMatch(query: string, text: string): FuzzyMatch | null {
  const q = query.toLowerCase();
  const t = text.toLowerCase();
  if (!q) {
    return { score: 0, indices: [] };
  }
  let best: FuzzyMatch | null = null;
  for (let anchor = t.indexOf(q[0]); anchor !== -1; anchor = t.indexOf(q[0], anchor + 1)) {
    const candidate = greedyFrom(q, t, anchor);
    if (candidate && (!best || candidate.score > best.score)) {
      best = candidate;
    }
  }
  return best;
}

function greedyFrom(q: string, t: string, anchor: number): FuzzyMatch | null {
  const indices: number[] = [];
  let score = 0;
  let searchFrom = anchor;
  let previous = -2;
  for (const ch of q) {
    const found = t.indexOf(ch, searchFrom);
    if (found === -1) {
      return null;
    }
    if (found === previous + 1) {
      score += 3;
    }
    if (found === 0 || WORD_SEPARATORS.has(t[found - 1])) {
      score += 3;
    }
    indices.push(found);
    previous = found;
    searchFrom = found + 1;
  }
  return { score: score - Math.min(indices[0] ?? 0, 3), indices };
}

export interface FieldOptionMatch {
  target: FieldRefTarget;
  indices: number[];
}

/**
 * All fuzzy matches on `text` (the display name by default), best score first
 * (ties keep vocabulary order). No cap — the menu scrolls.
 */
export function fuzzyFilterFields(
  fields: FieldRefTarget[],
  query: string,
  text: (field: FieldRefTarget) => string = (field) => field.displayName
): FieldOptionMatch[] {
  const q = query.trim();
  if (!q) {
    return fields.map((target) => ({ target, indices: [] }));
  }
  return fields
    .flatMap((target) => {
      const match = fuzzyMatch(q, text(target));
      return match ? [{ target, match }] : [];
    })
    .sort((a, b) => b.match.score - a.match.score)
    .map(({ target, match }) => ({ target, indices: match.indices }));
}

/**
 * The fields the caret's position may reference — records never, they are
 * navigated (see optionsForQuery). In a list slot (REDUCE's second argument,
 * SIZE's argument, INDEX's or SUM's first) only lists make sense, so only
 * lists are offered; in INDEX's third argument only its own list's members,
 * and in SUM's second only the number-typed ones (SUM insists on a numeric
 * member); in KPI's slots only KPI names, then the period/scenario tokens its
 * config supports (every token when the KPI argument doesn't resolve —
 * validation explains); elsewhere, the records' fields plus the members of
 * lists whose REDUCE body the caret is inside — KPIs excluded, since a KPI is
 * only ever read through KPI(). The same scope rules validation enforces, so
 * the menu never suggests a chip that cannot validate.
 */
export function fieldsForContext(fields: FieldRefTarget[], calls: EnclosingCall[]): FieldRefTarget[] {
  const call = calls[0];
  if (isListSlot(call)) {
    return fields.filter((field) => field.type === 'list');
  }
  if (isKpiNameSlot(call)) {
    return fields.filter((field) => field.kpi);
  }
  if (isKpiPeriodSlot(call) || isKpiScenarioSlot(call)) {
    const kpi = kpiSlotKpi(call, (ref) => {
      const trimmed = ref.trim();
      const target =
        fields.find((f) => f.kpi && f.path === trimmed) ??
        fields.find((f) => f.kpi && f.displayName.toLowerCase() === trimmed.toLowerCase());
      return target?.kpi;
    });
    const targets = isKpiPeriodSlot(call) ? KPI_PERIOD_TARGETS : KPI_SECTION_TARGETS;
    const available: string[] | undefined = kpi && (isKpiPeriodSlot(call) ? kpi.periods : kpi.sections);
    return targets.filter((target) => !available || available.includes(target.path));
  }
  const lookups = buildRefLookups(fields);
  const resolveListPath = (ref: string) => {
    const list = resolveRef(ref, lookups);
    return list?.type === 'list' ? list.path : undefined;
  };
  const memberList = memberSlotList(call, resolveListPath);
  if (memberList !== undefined) {
    return fields.filter(
      (field) => field.list?.path === memberList && (call?.fn !== 'SUM' || field.type === 'number')
    );
  }
  const inScope = listsInScope(calls, resolveListPath);
  return fields.filter(
    (field) => !isFormulaRecord(field) && !field.kpi && (!field.list || inScope.includes(field.list.path))
  );
}

/** One menu row: what it inserts, or — for a record, or a list whose items are in scope — descends into. */
export interface FieldOption extends FieldOptionMatch {
  /** The row's text: the entry's own name under what is browsed, or its full display path in a narrow slot. */
  primary: string;
  navigate: boolean;
}

/** "Company.Fu" → the record being browsed and the term filtering its keys; "Fu" → the root. */
export function splitFieldQuery(query: string): { prefix: string; term: string } {
  const dot = query.lastIndexOf('.');
  return dot === -1
    ? { prefix: '', term: query }
    : { prefix: query.slice(0, dot), term: query.slice(dot + 1) };
}

/**
 * The rows the caret's position and the typed query allow. A narrow slot
 * (list, member, KPI) offers its leaf candidates outright, by full display
 * path, since only one kind of chip parses there. Anywhere else the query
 * browses records: at the root, every record with a legal field beneath, the
 * root-level imports (formula fields) the position allows, and — inside a
 * REDUCE body — the list whose items are in scope, standing for the current
 * item; under "Record." its fields, under "List." the item's members.
 * Records and in-scope lists descend on select rather than chip; a list met
 * under its record is a plain chip (validation explains its placement). An
 * unknown prefix offers nothing.
 */
export function optionsForQuery(
  fields: FieldRefTarget[],
  calls: EnclosingCall[],
  query: string
): FieldOption[] {
  const legal = fieldsForContext(fields, calls);
  if (slotKind(calls[0]) === 'field') {
    return fuzzyFilterFields(legal, query, (field) => field.displayPath).map((match) => ({
      ...match,
      primary: match.target.displayPath,
      navigate: false,
    }));
  }
  const legalPaths = new Set(legal.map((field) => field.path));
  const hasLegalBeneath = (field: FieldRefTarget) =>
    legal.some((leaf) => leaf.path.startsWith(`${field.path}.`));
  const browsable = (field: FieldRefTarget) =>
    (isFormulaRecord(field) || field.type === 'list') && hasLegalBeneath(field);
  const { prefix, term } = splitFieldQuery(query);
  const browsed = prefix === '' ? undefined : browsedEntry(fields.filter(browsable), prefix);
  if (prefix !== '' && !browsed) {
    return [];
  }
  const options: { field: FieldRefTarget; navigate: boolean }[] = [];
  for (const field of fields) {
    if (browsed !== undefined) {
      if (field.parent === browsed.path && legalPaths.has(field.path)) {
        options.push({ field, navigate: false });
      }
    } else if (isFormulaRecord(field) || field.type === 'list') {
      if (browsable(field)) {
        options.push({ field, navigate: true });
      }
    } else if (field.parent === undefined && legalPaths.has(field.path)) {
      options.push({ field, navigate: false });
    }
  }
  const navigateByPath = new Map(options.map(({ field, navigate }) => [field.path, navigate]));
  return fuzzyFilterFields(
    options.map(({ field }) => field),
    term,
    displaySegmentOf
  ).map((match) => ({
    ...match,
    primary: displaySegmentOf(match.target),
    navigate: navigateByPath.get(match.target.path) ?? false,
  }));
}

/** The browsable entry a typed prefix names (see browsePath), in any case; undefined when it names none, or several. */
function browsedEntry(browsable: FieldRefTarget[], prefix: string): FieldRefTarget | undefined {
  const key = displayPathKey(prefix);
  const matches = browsable.filter((field) => displayPathKey(browsePath(field)) === key);
  return matches.length === 1 ? matches[0] : undefined;
}

/** How an entry is browsed: a record by its display path, a list by its own name — the item record its members hang off. */
function browsePath(target: FieldRefTarget): string {
  return isFormulaRecord(target) ? target.displayPath : displaySegmentOf(target);
}

/** The text that descends into `target`: the trigger re-fires on it and lists the children. */
export function browseText(target: FieldRefTarget): string {
  return `[${browsePath(target)}.`;
}

class FieldMenuOption extends MenuOption {
  constructor(readonly option: FieldOption) {
    super(option.target.path);
  }
}

/**
 * A KPI period/scenario slot that has exactly one legal option completes
 * itself: the moment "[" opens the menu (nothing typed yet), the option is
 * inserted instead of a one-row dropdown — e.g. the scenario of an
 * Actual-only KPI, or the period of a single-period one.
 */
export function autoInsertTarget(
  query: string | null,
  calls: EnclosingCall[],
  options: { target: FieldRefTarget }[]
): FieldRefTarget | undefined {
  const slot = isKpiPeriodSlot(calls[0]) || isKpiScenarioSlot(calls[0]);
  return slot && query === '' && options.length === 1 ? options[0].target : undefined;
}

/**
 * What to insert after a chip lands in `call`'s argument, to carry the caret
 * into the next one — null to stop there. Only a field-only slot is finished
 * by the chip alone (elsewhere it may be one operand of a longer expression),
 * and only an empty tail is safe to append to, so editing a written formula
 * never rewrites what follows.
 */
export function advanceAfterChip(call: EnclosingCall | undefined, tail: string): string | null {
  if (!call || slotKind(call) !== 'field' || tail.trim() !== '') {
    return null;
  }
  const spec = BUILTINS.get(call.fn);
  if (!spec) {
    return null;
  }
  const { min: minArgs, max: maxArgs } = arity(spec);
  const argIndex = call.argIndex + 1;
  if (argIndex >= maxArgs) {
    return ')';
  }
  const kind = slotKind({ ...call, argIndex });
  if (kind !== 'free') {
    return `, ${SLOT_OPENER[kind]}`;
  }
  // A free argument names nothing to open; the comma alone is certain, and
  // only while arity still demands one — INDEX's position, after its list.
  return argIndex < minArgs ? ', ' : null;
}

/**
 * Apply advanceAfterChip with the caret right after `chip`; call inside
 * editor.update. Lexical redirects an insert at a token boundary onto a new
 * sibling, so the chip itself is left intact.
 */
export function $advanceAfterChip(chip: FieldChipNode): void {
  const { display } = $serializeFormula();
  const caret = $displayOffsetOf(chip, chip.getTextContentSize());
  const advance = advanceAfterChip(findEnclosingCalls(display, caret)[0], display.slice(caret));
  const selection = $getSelection();
  if (advance !== null && $isRangeSelection(selection)) {
    selection.insertText(advance);
  }
}

/**
 * Replace the unclosed "[query" before the collapsed caret with `node`; call
 * inside editor.update. Works from the live text rather than the typeahead's
 * own match: that match commits in a React transition, a beat behind the
 * rows it drives, so a selection made in between would have the plugin split
 * the wrong span (its split-off node is ignored here for the same reason).
 * The trigger may already straddle text nodes from such a split; the
 * preceding simple-text siblings are merged back until the "[" is at hand.
 * Returns false when no trigger ends at the caret (the state already changed).
 */
function $replaceTriggerBeforeCaret(node: TextNode): boolean {
  const selection = $getSelection();
  if (!$isRangeSelection(selection) || !selection.isCollapsed()) {
    return false;
  }
  const anchorNode = selection.anchor.getNode();
  if (!$isTextNode(anchorNode) || !anchorNode.isSimpleText()) {
    return false;
  }
  const caret = $displayOffsetOf(anchorNode, selection.anchor.offset);
  if (!matchFieldTrigger($serializeFormula().display.slice(0, caret))) {
    return false;
  }
  let target = anchorNode;
  let offset = selection.anchor.offset;
  while (!target.getTextContent().slice(0, offset).includes('[')) {
    const previous = target.getPreviousSibling();
    if (!$isTextNode(previous) || !previous.isSimpleText()) {
      return false;
    }
    offset += previous.getTextContentSize();
    target = previous.mergeWithSibling(target);
  }
  const start = target.getTextContent().lastIndexOf('[', offset - 1);
  let refNode: TextNode;
  if (start === 0) {
    [refNode] = target.splitText(offset);
  } else {
    [, refNode] = target.splitText(start, offset);
  }
  refNode.replace(node);
  return true;
}

/**
 * The chip for `target` in place of the trigger before the caret, carried
 * into the next argument where the slot is done; call inside editor.update.
 * A no-op when the caret is not right after a field trigger.
 */
function $replaceFieldTriggerWithChip(target: FieldRefTarget): void {
  const chip = $createFieldChipNode(target.path, target.displayPath, fieldChipVariant(target));
  if (!$replaceTriggerBeforeCaret(chip)) {
    return;
  }
  chip.select();
  $advanceAfterChip(chip);
}

interface FieldTypeaheadPluginProps {
  fields: FieldRefTarget[];
  onOpenChange?: (open: boolean) => void;
}

export function FieldTypeaheadPlugin({ fields, onOpenChange }: FieldTypeaheadPluginProps) {
  const [editor] = useLexicalComposerContext();
  const [query, setQuery] = useState<string | null>(null);
  const [enclosingCalls, setEnclosingCalls] = useState<EnclosingCall[]>([]);

  // null = no trigger match ('' is a bare "["): render nothing and report
  // closed, so a dropped close inside the plugin can't leave a stale menu.
  const handleQueryChange = useCallback(
    (nextQuery: string | null) => {
      setQuery(nextQuery);
      if (nextQuery === null) {
        onOpenChange?.(false);
        return;
      }
      // Read first, set after: a state update inside the read can flush the
      // auto-insert effect while the read-only context is still on the stack.
      const calls = editor.getEditorState().read(() => $enclosingCallsAtCaret());
      setEnclosingCalls(calls);
    },
    [editor, onOpenChange]
  );

  const options = useMemo(
    () =>
      query === null
        ? []
        : optionsForQuery(fields, enclosingCalls, query).map((option) => new FieldMenuOption(option)),
    [fields, query, enclosingCalls]
  );

  const autoInsert = autoInsertTarget(
    query,
    enclosingCalls,
    options.map((option) => option.option)
  );
  useEffect(() => {
    if (!autoInsert) {
      return;
    }
    editor.update(() => $replaceFieldTriggerWithChip(autoInsert));
  }, [autoInsert, editor]);

  const onSelectOption = useCallback(
    (menuOption: FieldMenuOption, nodeToReplace: TextNode | null, closeMenu: () => void) => {
      const { target, navigate } = menuOption.option;
      editor.update(() => {
        const node = navigate
          ? $createTextNode(browseText(target))
          : $createFieldChipNode(target.path, target.displayPath, fieldChipVariant(target));
        if (!$replaceTriggerBeforeCaret(node)) {
          // No trigger at the caret: fall back to the plugin's own reading.
          if (nodeToReplace) {
            nodeToReplace.replace(node);
          } else {
            $insertNodes([node]);
          }
        }
        node.select();
        if (navigate) {
          // Descend: the trigger re-fires on "[Record." and the menu, still
          // open, lists the children — so the menu is deliberately not closed.
          return;
        }
        $advanceAfterChip(node as FieldChipNode);
        closeMenu();
      });
    },
    [editor]
  );

  const triggerFn = useCallback((text: string) => matchFieldTrigger(text), []);

  return (
    <LexicalTypeaheadMenuPlugin<FieldMenuOption>
      onQueryChange={handleQueryChange}
      onSelectOption={onSelectOption}
      triggerFn={triggerFn}
      // Function names are text entities; "[" or a name typed right after one
      // ("IF([", "REDUCE(ro") must still open the menus.
      ignoreEntityBoundary
      options={options}
      onOpen={() => onOpenChange?.(true)}
      onClose={() => onOpenChange?.(false)}
      menuRenderFn={(anchorRef, { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }) =>
        // An auto-inserting slot never shows its one row — the effect above
        // replaces the trigger with that chip instead.
        anchorRef.current && options.length && !autoInsert
          ? createPortal(
              <TypeaheadMenu
                options={options}
                toItem={({ option }) => ({
                  key: option.target.path,
                  primary: option.primary,
                  primaryMatches: option.indices,
                  badge: SOURCE_BADGE[option.target.source],
                  type: option.target.type,
                  detail: renderFieldDetail(option.target),
                })}
                selectedIndex={selectedIndex}
                onHighlight={setHighlightedIndex}
                onSelect={selectOptionAndCleanUp}
              />,
              anchorRef.current
            )
          : null
      }
    />
  );
}
